import type { PartialDeep } from 'type-fest';

import type { LoginState } from '@change-corgi/middle-end-gql-schema';

import type { LoginOrJoinMethod } from 'src/app/shared/utils/loginOrJoin';

import type { useStartAPetitionState } from './hooks';

export const MediaTypes = {
	AI: 'AI_GENERATED',
	USER: 'USER_UPLOADED',
} as const;
type MetadataUser = {
	id?: string;
	loginOrJoinMethod?: LoginOrJoinMethod;
	initialLoginState?: Extract<LoginState, 'AUTHENTICATED' | 'GUEST'>;
};

export type Recommendation = {
	id: string;
	recommendation: string;
	why: string;
};

export type Draft = PartialDeep<{
	id: string;
	authorId: string;
	type:
		| 'ai_draft'
		| 'user_draft'
		| 'recommendations_variant_b_draft'
		| 'recommendations_variant_b_draft_ai'
		| 'recommendations_variant_b_draft_user'
		| null;
	input: {
		issuePrompt: string;
		personalStory: string;
		recommendations?: Recommendation[] | null;
		selectedRecommendation?: string;
	};
	recommendedDraft: {
		ask: string;
		description: string;
		image: { url: string; type: 'AI_GENERATED' } | null;
	} | null;
	petitionDraft: {
		ask: string | null;
		description: string | null;
		image: { url: string; type: 'USER_UPLOADED' | 'AI_GENERATED' } | null;
		reach: 'local' | 'national' | 'global';
		location: { id: string; name: string } | null;
	} | null;
	metadata: {
		draftMode: 'recommended_draft' | 'user_draft';
		imageJobId: string | null;
		imageFetchCount: number | null;
		imageGenerationCount: number;
		recommendationsJobId: string | null;
		user?: MetadataUser;
		variant?: 'control' | 'recommendations_variant_b';
	};
}>;

export type StarterOnboardingPageState = ReturnType<typeof useStartAPetitionState>;

export type StateInput = object;

export type StateContext = { draft?: Draft | null };

export type StateEvents =
	| { type: 'back'; target?: null }
	| { type: 'submit'; draft?: Draft; target?: string | null }
	| { type: 'error' }
	| { type: 'sync'; step: string };

export type StateActions = {
	onSubmit: () => void;
	onError: () => void;
	persistDraftType: () => void;
	persistRecommendationsDraftType: () => void;
	unsetDraftType: () => void;
	unsetRecommendationsDraftType: () => void;
};

export type StateGuards = {
	transitionTo: (_: unknown, params: { target: string }) => boolean;
	isUserDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isRecommendationsVariantBUserDraft: (
		args: { context: StateContext; event: StateEvents },
		params?: unknown,
	) => boolean;
};
